import type { CSSProperties, ComponentType } from 'react';
import styles from './FilterPillGroup.module.css';

const PILL_ICON_SIZE = 14;
const PILL_ICON_STROKE = 1.8;

export type FilterPillPalette = {
  accent: string;
  bg: string;
  border: string;
};

export type FilterPillTokenPalette = {
  useToken: true;
  cssColor: string;
  cssBg: string;
  cssBorder: string;
};

export type FilterPillDef = {
  value: string;
  label: string;
  /** Native tooltip — what this filter means. */
  hint?: string;
  icon: ComponentType<{
    size?: number | string;
    stroke?: number;
    className?: string;
    'aria-hidden'?: boolean;
  }>;
} & (FilterPillPalette | FilterPillTokenPalette);

export type FilterPillOption = FilterPillDef & {
  count?: number;
};

function isTokenPalette(opt: FilterPillDef): opt is FilterPillDef & FilterPillTokenPalette {
  return 'useToken' in opt && opt.useToken === true;
}

function pillActiveStyle(opt: FilterPillDef, active: boolean): CSSProperties {
  if (!active) {
    return {};
  }
  if (isTokenPalette(opt)) {
    return {
      borderColor: `color-mix(in srgb, ${opt.cssBorder} 22%, var(--wp-border-hairline))`,
      background: `color-mix(in srgb, ${opt.cssBg} 11%, var(--wp-surface-card))`,
      color: opt.cssColor,
    };
  }
  return {
    borderColor: opt.border,
    background: opt.bg,
    color: opt.accent,
  };
}

export type FilterPillGroupProps = {
  options: readonly FilterPillOption[];
  value: string;
  onChange: (value: string) => void;
  'aria-label': string;
  className?: string;
  showCount?: boolean;
  /** Horizontal scroll row (filter dock on narrow viewports). */
  layout?: 'wrap' | 'scroll' | 'grid';
};

export function FilterPillGroup({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
  showCount = true,
  layout = 'wrap',
}: FilterPillGroupProps) {
  return (
    <div
      className={
        className
          ? `${styles.group} ${layout === 'scroll' ? styles.groupScroll : ''} ${layout === 'grid' ? styles.groupGrid : ''} ${className}`
          : `${styles.group} ${layout === 'scroll' ? styles.groupScroll : ''} ${layout === 'grid' ? styles.groupGrid : ''}`
      }
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value || '_all'}
            type="button"
            role="radio"
            aria-checked={active}
            title={opt.hint}
            aria-description={opt.hint}
            className={active ? `${styles.pill} ${styles.pillActive}` : styles.pill}
            style={pillActiveStyle(opt, active)}
            onClick={() => onChange(opt.value)}
          >
            <span className={styles.pillIcon}>
              <Icon size={PILL_ICON_SIZE} stroke={PILL_ICON_STROKE} aria-hidden />
            </span>
            <span className={styles.pillLabel}>{opt.label}</span>
            {showCount && opt.count !== undefined ? (
              <span className={styles.count}>({opt.count})</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
